<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>

        <div v-else>
            <vue-headful :title="(id ? 'Edit' : 'Create') + ' Reminder [JSA] :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/jsa`">
                            <i class="fa fa-files-o"></i> JSA
                        </router-link>
                    </li>
                    <template v-if="reminder.id">
                        <li class="breadcrumb-item active">
                            <i class="fa fa-edit"></i> Edit: {{ reminder.reminder_name }}
                        </li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active">
                            <i class="fa fa-plus-circle"></i> New Reminder
                        </li>
                    </template>
                </ol>
            </h4>

            <div class="row">
                <div class="col-xl-9 col-lg-10 col-md-12 mb-4">

                    <div class="card mb-4">
                        <div class="card-header">Reminder:</div>
                        <div class="card-body">
                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.reminder_name, 'text-danger': errors.reminder_name}">
                                    Reminder Name: <i class="fa fa-sm fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-input v-model="reminder.reminder_name" placeholder="Enter Reminder Name" :class="{'is-invalid': errors.reminder_name}" />

                                    <div v-if="errors.reminder_name" class="text-danger mt-2">
                                        <div v-for="err in errors.reminder_name">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right text-muted">
                                    Active:
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-checkbox v-model="reminder.is_active" :value="1" :unchecked-value="0" plain />
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.asset_id, 'text-danger': errors.asset_id}">
                                    JSA Template:
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-select v-model="reminder.asset_id" :options="templateOptions" :class="{'is-invalid': errors.asset_id}" />

                                    <small class="text-info">
                                        Leave this blank if you want to create a general reminder to create at least 1 JSA from the templates list.
                                    </small>

                                    <div v-if="errors.asset_id" class="text-danger mt-2">
                                        <div v-for="err in errors.asset_id">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right text-muted">
                                    Reminder Description:
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-textarea v-model="reminder.reminder_description" placeholder="Enter Reminder Description (Optional)" />
                                    <div class="text-muted small mt-1"><strong>Note:</strong> The Description will be included in the reminder email send to the recipients.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4">
                        <div class="card-header">Interval Settings:</div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-md-6 offset-md-3">
                                    <div class="alert alert-info">
                                        If the selected recipients:

                                        <ul>
                                            <li>
                                                have not completed
                                                <template v-if="selectedJsa"> a <strong>{{ selectedJsa }}</strong> JSA</template>
                                                <template v-else>any JSA</template>
                                            </li>
                                            <li>every <strong>{{ reminder.reminder_interval }}</strong></li>
                                            <li v-if="reminder.reminder_interval === 'Week'">on <strong>{{ selectedWeekday }}</strong></li>
                                            <li v-if="reminder.reminder_interval === 'Month'">on the <strong>{{ selectedMonthday }}</strong></li>
                                            <li>by <strong>{{ selectedTime }}</strong></li>
                                        </ul>

                                        Send an Email Reminder.
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.reminder_interval, 'text-danger': errors.reminder_interval}">
                                    Every: <i class="fa fa-sm fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-select v-model="reminder.reminder_interval" :options="intervalOptions" :class="{'is-invalid': errors.reminder_interval}" />

                                    <div v-if="errors.reminder_interval" class="text-danger mt-2">
                                        <div v-for="err in errors.reminder_interval">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.reminder_time, 'text-danger': errors.reminder_time}">
                                    By: <i class="fa fa-sm fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-form-timepicker v-model="reminder.reminder_time" :class="{'is-invalid': errors.reminder_time}" :minutes-step="15" />

                                    <span class="text-muted small">The time selected will be checked against the users' timezone. So a value of 5pm will to users at 5pm in their respective timezone.</span>

                                    <div v-if="errors.reminder_time" class="text-danger mt-2">
                                        <div v-for="err in errors.reminder_time">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <template v-if="reminder.reminder_interval === 'Day'">
                                <div class="form-group row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right text-muted">
                                        Exclude Saturday:
                                    </label>
                                    <div class="col-md-6 col-sm-8">
                                        <b-form-checkbox v-model="reminder_config.exclude_saturday" :value="true" :unchecked-value="false" plain />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right text-muted">
                                        Exclude Sunday:
                                    </label>
                                    <div class="col-md-6 col-sm-8">
                                        <b-form-checkbox v-model="reminder_config.exclude_sunday" :value="true" :unchecked-value="false" plain />
                                    </div>
                                </div>
                            </template>

                            <template v-if="reminder.reminder_interval === 'Week'">
                                <div class="form-group row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.reminder_day, 'text-danger': errors.reminder_day}">
                                        On: <i class="fa fa-sm fa-asterisk text-danger"></i>
                                    </label>
                                    <div class="col-md-6 col-sm-8">
                                        <b-form-select v-model="reminder.reminder_day" :options="weekDayOptions" :class="{'is-invalid': errors.reminder_day}" />

                                        <div v-if="errors.reminder_day" class="text-danger mt-2">
                                            <div v-for="err in errors.reminder_day">
                                                {{ err }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template v-if="reminder.reminder_interval === 'Month'">
                                <div class="form-group row">
                                    <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right" :class="{'text-muted': !errors.reminder_day, 'text-danger': errors.reminder_day}">
                                        On: <i class="fa fa-sm fa-asterisk text-danger"></i>
                                    </label>
                                    <div class="col-md-6 col-sm-8">
                                        <b-form-select v-model="reminder.reminder_day" :options="monthDayOptions" :class="{'is-invalid': errors.reminder_day}" />

                                        <div v-if="errors.reminder_day" class="text-danger mt-2">
                                            <div v-for="err in errors.reminder_day">
                                                {{ err }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div class="form-group row">
                                <label class="col-lg-3 col-md-4 col-sm-5 col-form-label text-sm-right text-muted">
                                    Emails to CC:
                                </label>
                                <div class="col-md-6 col-sm-8">
                                    <b-input-group>
                                        <b-form-input ref="addEmail" v-model="additional_email" placeholder="Enter Additional Emails to cc on the Reminder Alert" />
                                        <b-input-group-append>
                                            <b-button variant="info" v-on:click="addAdditionalEmail()">Add</b-button>
                                        </b-input-group-append>
                                    </b-input-group>

                                    <span class="text-muted small">Enter Additional Emails to cc on the Reminder.</span>

                                    <ul class="list-unstyled mt-2">
                                        <li v-for="(email, i) in reminder_config.additional_emails" class="mb-1">
                                            <button type="button" class="btn btn-xs btn-default" v-on:click="removeAdditionalEmail(i)"><i class="fa fa-trash text-danger"></i></button> {{ email }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header">Recipients ({{ recipients.length }}):</div>
                        <div class="card-body">
                            <div v-if="errors.recipients" class="text-danger mt-2">
                                <div v-for="err in errors.recipients">
                                    {{ err }}
                                </div>
                            </div>

                            <div v-for="(recip, i) in recipients" class="row form-group">
                                <div class="col-md-1">
                                    <button class="btn btn-sm btn-default" v-on:click="removeRecipient(i)"><i class="fa fa-trash text-danger"></i></button>
                                </div>
                                <div class="col-md-5">
                                    <b-form-select v-model="recip.recipient_type" size="sm" :options="recipientTypes" :class="{'is-invalid': errors['recipients.'+i+'.recipient_type']}" />

                                    <div v-if="errors['recipients.'+i+'.recipient_type']" class="text-danger mt-2">
                                        <div v-for="err in errors['recipients.'+i+'.recipient_type']">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-5" v-if="recip.recipient_type">
                                    <b-form-select v-model="recip.recipient_id" size="sm" :options="recipOptions(recip.recipient_type)" :class="{'is-invalid': errors['recipients.'+i+'.recipient_id']}" />

                                    <div v-if="errors['recipients.'+i+'.recipient_id']" class="text-danger mt-2">
                                        <div v-for="err in errors['recipients.'+i+'.recipient_id']">
                                            {{ err }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="text-center" style="padding: 6px 8px; border: 1px dashed #ccc;" v-on:click="addRecipient()">
                                <i class="fa fa-plus-circle text-success"></i> Add Recipient
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button type="button" class="btn btn-sm btn-success" v-on:click="saveReminder()" :disabled="saving"><i class="fa fa-save"></i> Save Reminder</button>

        </div>
    </div>
</template>


<script>
    import moment from 'moment-timezone';

    export default {
        data() {
            return {
                loading: true,
                saving: false,
                cloning: false,
                errors: {},
                id: this.$route.params.id,
                reminder: {},
                additional_email: null,

                templates: [],
                divisions: [],
                roles: [],
                users: [],
                recipients: [],

                intervalOptions: [
                    {value: null, text: '-- Select Interval --'},
                    {value: 'Day', text: 'Day'},
                    {value: 'Week', text: 'Week'},
                    {value: 'Month', text: 'Month'},
                ],

                weekDayOptions: [
                    {value: null, text: '-- Select Day of the Week --'},
                    {value: 0, text: 'Sunday'},
                    {value: 1, text: 'Monday'},
                    {value: 2, text: 'Tuesday'},
                    {value: 3, text: 'Wednesday'},
                    {value: 4, text: 'Thursday'},
                    {value: 5, text: 'Friday'},
                    {value: 6, text: 'Saturday'},
                ],

                reminder_config: {
                    exclude_saturday: false,
                    exclude_sunday: false,
                    additional_emails: [],
                },

                recipientTypes: [
                    {value: null, text: '-- Select Recipient Type --'},
                    {value: 'Division', text: 'Division'},
                    {value: 'Role', text: 'Role'},
                    {value: 'User', text: 'User'},
                ],
            }
        },

        watch: {
            'reminder.reminder_time'(newval, oldVal){
                //Testing
                //console.log(newval);
                //console.log(this.reminder.reminder_time);
            }
        },

        computed: {

            userOptions(){
                //Setup
                var options = [
                    {value: null, text: '-- Select a User --'},
                ];

                //Loop
                for(var i in this.users)
                {
                    options.push({value: this.users[i].UserId, text: (this.users[i].LastName+', '+this.users[i].FirstName+' - '+this.users[i].email)});
                }

                //SEnd it
                return options;
            },

            templateOptions(){
                //Setup
                var options = [
                    {value: null, text: '-- Check for Any JSA --'},
                ];

                //Loop
                for(var i in this.templates)
                {
                    options.push({value: this.templates[i].jsa_id, text: (this.templates[i].jsa_name)});
                }

                //SEnd it
                return options;
            },

            monthDayOptions(){
                //Setup
                var options = [
                    {value: null, text: '-- Select Day of the Month --'},
                ];

                //Loop
                for(var i = 1; i <= 31; i++)
                {
                    options.push({value: i, text: i});
                }

                //SEnd it
                return options;
            },

            selectedJsa(){
                //Check for null
                if(!this.reminder.asset_id)
                {
                    return null;
                }

                //Loop
                for (var i = 0; i < this.templates.length; i++)
                {
                    //Check it
                    if(this.templates[i].jsa_id == this.reminder.asset_id)
                    {
                        return this.templates[i].jsa_name;
                    }
                }

                //Default Send empty
                return null;
            },

            selectedWeekday(){
                //Check for null
                if(!this.reminder.reminder_interval)
                {
                    return '';
                }

                //Loop
                for (var i = 0; i < this.weekDayOptions.length; i++)
                {
                    //Check it
                    if(this.weekDayOptions[i].value == this.reminder.reminder_day)
                    {
                        return this.weekDayOptions[i].text;
                    }
                }

                //Default Send empty
                return '';
            },

            selectedMonthday(){
                //Check for null
                if(!this.reminder.reminder_interval)
                {
                    return '';
                }

                //Loop
                for (var i = 0; i < this.monthDayOptions.length; i++)
                {
                    //Check it
                    if(this.monthDayOptions[i].value == this.reminder.reminder_day)
                    {
                        return this.monthDayOptions[i].text;
                    }
                }

                //Default Send empty
                return '';
            },

            selectedTime(){
                //Check for empty
                if(!this.reminder.reminder_time)
                {
                    return '';
                }

                //Parse the
                var datetime = "2023-03-15 " + this.reminder.reminder_time;

                //SEtu
                var mo = moment(datetime);

                //Send it
                return mo.format('hh:mm a');
            },
        },


        methods: {

            getReminder() {
                //Get the data
                this.$http
                    .get('jsa/reminders/edit' + (this.id ? '/'+this.id : ''))
                    .then(response => {
                        //Set all the data
                        this.reminder = response.data.reminder;
                        this.recipients = response.data.recipients;

                        //Check for empty
                        if(this.recipients.length === 0)
                        {
                            //Add
                            this.addRecipient();
                        }

                        //Check fi Config is Set
                        if(this.reminder.reminder_config)
                        {
                            //Set the Config
                            this.reminder_config = this.reminder.reminder_config;
                        }

                        //Set resources
                        this.templates = response.data.templates;
                        this.divisions = response.data.divisions;
                        this.roles = response.data.roles;
                        this.users = response.data.users;
                    })
                    .catch(err => {
                        //Log the error
                        console.log(err);
                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveReminder() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Push other stuff
                this.reminder.recipients = this.recipients;
                this.reminder.reminder_config = this.reminder_config;

                //Send it
                this.$http
                    .post('jsa/reminders/edit', this.reminder)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Reminder Saved');

                        //Return to main list?
                        this.$router.push('/jsa#reminders');
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;
                    });
            },

            cloneTemplate(){
                //Send it
                this.cloning = true;

                //Send it
                this.$http
                    .post('/jsa/clone/' + this.reminder_template_id)
                    .then(response => {
                        //Return to main list?
                        this.$router.push('/jsa/edit/' + response.data.id);
                    })
                    .catch(response => {

                    })
                    .then(response => {
                        //Rest
                        this.cloning = false;
                    });
            },

            addRecipient(){
                //Push
                this.recipients.push({
                    recipient_type: null,
                    recipient_id: null,
                });
            },

            removeRecipient(i){
                //Splice it
                this.recipients.splice(i, 1);
            },

            addAdditionalEmail(){
                //Check for null or empty
                if(!this.additional_email)
                {
                    //Alert?
                    this.$toastr.warning('Enter An Email Address');

                    //STop here
                    return;
                }

                //Push it
                this.reminder_config.additional_emails.push(this.additional_email);

                //Reset it
                this.additional_email = null;

                //Set the Focus back on the input
                this.$refs.addEmail.focus();
            },

            removeAdditionalEmail(i){
                //Splice it
                this.reminder_config.additional_emails.splice(i, 1);
            },

            recipOptions(asset_type){
                //Setup
                var options = [
                    {value: null, text: '-- Select Recipient Type --'}
                ];

                //Switch depending ont he type
                switch(asset_type)
                {
                    case 'Division':
                        //Setup
                        options = [
                            {value: null, text: '-- Select Division --'}
                        ];

                        //Loop
                        for(var i in this.divisions)
                        {
                            options.push({
                                value: this.divisions[i].DivisionId,
                                text: ((this.divisions[i].division_code ? this.divisions[i].division_code + ' - ' : '') + this.divisions[i].DivisionName)
                            });
                        }
                        break;

                    case 'Role':
                        //Setup the Roles
                        //Setup
                        options = [
                            {value: null, text: '-- Select Role --'}
                        ];

                        //Loop
                        for(var i in this.roles)
                        {
                            options.push({
                                value: this.roles[i].id,
                                text: this.roles[i].name
                            });
                        }
                        break;

                    case 'User':
                        //Set using the function
                        options = this.userOptions;
                        break;
                }

                //Send it
                return options;
            }
        },

        mounted(){
            //Get the data
            this.getReminder();
        },

    }
</script>